/**
 * Model provider settings. Admin only.
 *
 * The API key field is **write-only**: you can set a key, and afterwards you see
 * `has_key` and the last four characters. That is enough to recognise which key is
 * installed and never enough to use it. This view must not try to be helpful by
 * caching what was typed — a compromised browser session is exactly the case the
 * write-only contract exists for.
 *
 * The key field is also optional, deliberately: LM Studio and Ollama need none, and
 * the local-first path must never be blocked by cloud-credential machinery.
 */

import { useEffect, useState, type FormEvent } from "react";
import { api, type Provider } from "../lib/api";

const KINDS = ["openai_compatible", "google", "fixture"] as const;

export function Providers() {
  const [providers, setProviders] = useState<Provider[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);

  async function refresh() {
    try {
      setProviders(await api.providers());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function test(id: string) {
    setTesting(id);
    setError(null);
    try {
      await api.testProvider(id);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setTesting(null);
      await refresh();
    }
  }

  return (
    <main className="page">
      <h1>Model providers</h1>
      <p style={{ color: "var(--ink-soft)", maxWidth: "60ch" }}>
        One chat provider and one embedding provider. Point them at a local runtime like
        LM Studio, or at a cloud endpoint. Keys are stored encrypted and can never be
        read back through this interface.
      </p>

      {error && (
        <p data-testid="providers-error" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      )}

      <ul className="provider-list" data-testid="provider-list">
        {providers?.map((provider) => (
          <li key={provider.id} className="card" data-testid="provider-row" data-role={provider.role}>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <strong>{provider.label}</strong>
              <span className="eyebrow">{provider.role}</span>
            </div>
            <p className="mono" style={{ margin: 0, color: "var(--ink-soft)" }}>
              {provider.kind} · {provider.model} · {provider.base_url}
            </p>
            <p className="mono" style={{ margin: 0 }} data-testid="provider-key">
              {provider.has_key ? `key ••••${provider.last4 ?? ""}` : "no key (local provider)"}
              {provider.enabled ? " · enabled" : " · disabled"}
            </p>
            {provider.last_tested_at && (
              <p
                className="mono"
                data-testid="provider-test-result"
                style={{ margin: 0, color: provider.last_test_ok ? "var(--human)" : "var(--danger)" }}
              >
                {provider.last_test_ok ? "reachable" : "unreachable"} · {provider.last_test_detail}
              </p>
            )}
            <button onClick={() => void test(provider.id)} disabled={testing === provider.id}>
              {testing === provider.id ? "testing…" : "Test connection"}
            </button>
          </li>
        ))}
      </ul>

      <ProviderForm onSaved={refresh} onError={setError} />
    </main>
  );
}

function ProviderForm({
  onSaved,
  onError,
}: {
  onSaved(): Promise<void>;
  onError(message: string): void;
}) {
  const [role, setRole] = useState("chat");
  const [kind, setKind] = useState<(typeof KINDS)[number]>("openai_compatible");
  const [label, setLabel] = useState("");
  const [baseUrl, setBaseUrl] = useState("http://localhost:1234/v1");
  const [model, setModel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [enabled, setEnabled] = useState(true);

  async function save(event: FormEvent) {
    event.preventDefault();
    try {
      await api.putProvider({
        role,
        kind,
        label: label.trim(),
        base_url: baseUrl.trim(),
        model: model.trim(),
        enabled,
        // Send the key only when one was typed. An empty string would be a key.
        ...(apiKey ? { api_key: apiKey } : {}),
      });
      // Clear it the moment it leaves: nothing is gained by keeping a secret in a
      // component's state after the request that needed it has returned.
      setApiKey("");
      await onSaved();
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <form className="card compose" onSubmit={save} data-testid="provider-form">
      <h2>Set the provider for a role</h2>

      <label htmlFor="p-role" className="eyebrow">
        Role
      </label>
      <select id="p-role" value={role} onChange={(e) => setRole(e.target.value)}>
        <option value="chat">chat</option>
        <option value="embedding">embedding</option>
      </select>

      <label htmlFor="p-kind" className="eyebrow">
        Kind
      </label>
      <select
        id="p-kind"
        value={kind}
        onChange={(e) => setKind(e.target.value as (typeof KINDS)[number])}
      >
        {KINDS.map((k) => (
          <option key={k} value={k}>
            {k}
          </option>
        ))}
      </select>

      <label htmlFor="p-label" className="eyebrow">
        Label
      </label>
      <input id="p-label" value={label} onChange={(e) => setLabel(e.target.value)} required />

      <label htmlFor="p-url" className="eyebrow">
        Base URL
      </label>
      <input id="p-url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} required />

      <label htmlFor="p-model" className="eyebrow">
        Model
      </label>
      <input id="p-model" value={model} onChange={(e) => setModel(e.target.value)} required />

      <label htmlFor="p-key" className="eyebrow">
        API key — optional; local runtimes need none
      </label>
      <input
        id="p-key"
        type="password"
        autoComplete="off"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        placeholder="leave blank to keep the current key"
      />

      <label className="row" style={{ gap: ".4rem" }}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          style={{ width: "auto" }}
        />
        Enabled
      </label>

      <button className="primary" type="submit">
        Save provider
      </button>
    </form>
  );
}
